import type { FormAddress } from '@user/components/Formik/'
import type { CustomField, CustomFieldValue } from '@sales-center/common'
import { IEmailCollectionItem, IReferral } from '@sales-center/common'
import { ILeadInput } from '@user/store/lead/typings'
import {
  customValuesToFormModel,
  ICustomFieldValueFormModel,
} from '@user/components/CustomFields'
import {
  ICallTrackingActivity,
  ICallTrackingActivityDetail,
} from '@user/features/CampaignTracking/typings'
import { leadStatusDisqualified } from '@user/components/Settings/EntityFormSettings/constants'

export type ILeadFormModel = Pick<
  FormAddress,
  'country' | 'county' | 'lat' | 'lng'
> &
  ICustomFieldValueFormModel & {
    salesTeamId: string
    assigneeId: string | null
    salesFunnelId: string

    salesFunnelStageId: string
    campaignId: string | null
    campaign: string | null
    leadSourceId: string | null
    leadSource: string | null

    preferredMethodOfContact: string
    preferredTimeOfContact: string[]
    originalContactMethod: string
    note: string
    noteCode?: string
    addNoteToPestPac?: boolean
    serviceLocation?: string
    draftNote?: string
    tags: string
    nextActionDate: Date | null
    draftLeadId?: number
    viewDetail?: boolean
    externalLocationId?: string
    externalLocationCode?: string
    branchId?: string
    branch?: string
    submittedBy?: string
    submittedDate?: Date
    referral: IReferral | null
    type: string
    callTrackingActivity?: ICallTrackingActivity | null
    CallTrackingSystemFieldValues?: ICallTrackingActivityDetail[] | null
    webFormCallTrackingEventId?: string | null
    automatedAssignmentRuleId?: number

    //region Location
    altPhoneNumber: string
    altPhoneExt: string
    billingLocation: boolean
    city: string
    company: string
    contactMethod: string
    contactTime: string
    county: string
    division: string
    divisionId: string
    emailAddresses: IEmailCollectionItem[]
    externalLinkId: string
    externalLinkType: string
    firstName: string
    lastName: string
    latitude: number
    locationType: string
    locationTypeId: string
    longitude: number
    mobilePhone: string
    phoneExt: string
    phoneNumber: string
    postalCode: string
    primaryServiceLocation: boolean
    state: string
    address1: string
    address2: string
    title: string
    targetPests: string[]
    //endregion

    //region Bill To Location
    billTo_primaryServiceLocation: boolean
    billTo_billingLocation: boolean
    billTo_divisionId: string
    billTo_division: string
    billTo_locationTypeId: string
    billTo_locationType: string
    billTo_firstName: string
    billTo_lastName: string
    billTo_title: string
    billTo_company: string
    billTo_emailAddresses: IEmailCollectionItem[]
    billTo_phoneNumber: string
    billTo_phoneExt: string
    billTo_mobilePhone: string
    billTo_altPhoneNumber: string
    billTo_altPhoneExt: string
    billTo_address1: string
    billTo_address2: string
    billTo_city: string
    billTo_state: string
    billTo_postalCode: string
    billTo_county: string
    billTo_latitude: number
    billTo_longitude: number
    billTo_externalLinkId: string
    billTo_externalLinkType: string
    billTo_contactTime: string
    billTo_contactMethod: string
    billTo_targetPests: string[]
    billTo_externalLocationId?: string
    billTo_externalLocationCode?: string
    //endregion

    locationSameAsBillTo: boolean
    reasonId?: number | string
    customReason?: string
    salesFunnelStageName?: string
    importId?: number
    importName: string
    createNewLocation?: boolean
    fullAddress?: string
    contactLocationId?: number
    knockStatusLocationId?: number
  }

export const createInitialValues = (
  customFields: CustomField[],
  customFieldValues: CustomFieldValue[] = [],
  phone?: string | null,
  addNewlocation?: boolean,
): ILeadFormModel => ({
  country: '',
  lat: 0,
  lng: 0,
  salesTeamId: '',
  assigneeId: '',
  salesFunnelId: '',
  type: 'Manual',
  salesFunnelStageId: '',
  campaignId: '',
  leadSourceId: '',
  preferredMethodOfContact: '',
  preferredTimeOfContact: [],
  originalContactMethod: '',
  note: '',
  draftNote: '',
  tags: '',
  nextActionDate: new Date(),
  targetPests: [],
  campaign: '',
  leadSource: '',
  viewDetail: false,

  referral: null,
  callTrackingActivity: null,
  customFields: customValuesToFormModel(customFields, customFieldValues),
  webFormCallTrackingEventId: null,
  automatedAssignmentRuleId: undefined,
  CallTrackingSystemFieldValues: [],

  billingLocation: false,
  primaryServiceLocation: false,
  division: '',
  divisionId: '',
  locationTypeId: '',
  locationType: '',

  firstName: '',
  lastName: '',
  title: '',
  company: '',
  emailAddresses: [],
  phoneNumber: phone ?? '',
  phoneExt: '',
  mobilePhone: '',
  altPhoneNumber: '',
  altPhoneExt: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  postalCode: '',
  county: '',
  latitude: 0,
  longitude: 0,
  externalLinkId: '',
  externalLinkType: '',
  contactTime: '',
  contactMethod: '',
  //endregion

  //region Bill To Location

  billTo_billingLocation: true,
  billTo_primaryServiceLocation: true,
  billTo_division: '',
  billTo_divisionId: '',
  billTo_locationTypeId: '',
  billTo_locationType: '',

  billTo_firstName: '',
  billTo_lastName: '',
  billTo_title: '',
  billTo_company: '',
  billTo_emailAddresses: [],
  billTo_phoneNumber: '',
  billTo_phoneExt: '',
  billTo_mobilePhone: '',
  billTo_altPhoneNumber: '',
  billTo_altPhoneExt: '',
  billTo_address1: '',
  billTo_address2: '',
  billTo_city: '',
  billTo_state: '',
  billTo_postalCode: '',
  billTo_county: '',
  billTo_latitude: 0,
  billTo_longitude: 0,
  billTo_externalLinkId: '',
  billTo_externalLinkType: '',
  billTo_contactTime: '',
  billTo_contactMethod: '',
  billTo_targetPests: [],

  locationSameAsBillTo: true,
  reasonId: -1,
  customReason: '',
  salesFunnelStageName: '',
  importId: 0,
  importName: '',
  createNewLocation: addNewlocation,
  fullAddress: '',
})

function getDisqualifiedReason(
  model: ILeadFormModel,
): Pick<ILeadInput, 'reasonId' | 'customReason'> {
  const { salesFunnelStageName, reasonId, customReason } = model

  if (salesFunnelStageName !== leadStatusDisqualified) return {}
  if (reasonId === undefined || reasonId === '' || Number(reasonId) < 0)
    return {}

  const selectedReasonId = Number(reasonId)

  return {
    reasonId: selectedReasonId,
    customReason: selectedReasonId === 0 ? customReason : '',
  }
}

export function transform(
  model: ILeadFormModel,
  customFieldValues: ILeadInput['customFieldValues'],
  updateCoreLocation?: boolean,
): ILeadInput {
  return {
    assigneeId: model.assigneeId == 'unassigned' ? null : model.assigneeId,
    automatedAssignmentRuleId: model.automatedAssignmentRuleId,
    callTrackingSystemFieldValues: model.CallTrackingSystemFieldValues,
    campaign: model.campaign,
    campaignId: model.campaignId,
    importName: model.importName,
    importId: model.importId,
    createNewLocation: model.createNewLocation,
    customFieldValues,
    divisionId: model.divisionId,
    division: model.division,
    draftLeadId: model.draftLeadId,
    draftNote: model.draftNote,
    externalLocationCode: model.externalLocationCode,
    externalLocationId: model.externalLocationId,
    leadSource: model.leadSource,
    leadSourceId: model.leadSourceId,
    contactLocationId: model.contactLocationId ?? undefined,
    knockStatusLocationId: model.knockStatusLocationId ?? undefined,
    locations: [
      //region location
      {
        primaryServiceLocation: true,
        billingLocation: model.billingLocation,
        divisionId: model.divisionId,
        division: model.division,
        locationTypeId: model.locationTypeId,
        locationType: model.locationType,

        contact: {
          firstName: model.firstName,
          lastName: model.lastName,
          title: model.title,
          company: model.company,
          emailAddresses: model.emailAddresses,
          phoneNumber: model.phoneNumber,
          phoneExt: model.phoneExt,
          mobilePhone: model.mobilePhone,
          altPhoneNumber: model.altPhoneNumber,
          altPhoneExt: model.altPhoneExt,
          street1: model.address1,
          street2: model.address2,
          city: model.city,
          stateAbbreviation: model.state,
          postalCode: model.postalCode,
          county: model.county,
          latitude: model.lat,
          longitude: model.lng,
          externalLinkId:
            model.externalLinkId || model.externalLocationId || '',
          externalLinkType: model.externalLinkType || 'Location',
          contactTime: model.preferredTimeOfContact.join(','),
          contactMethod: model.preferredMethodOfContact,
          locationCode: model.externalLocationCode,
        },
      },
      //endregion

      //region BillTo Location
      {
        primaryServiceLocation: model.billTo_primaryServiceLocation,
        billingLocation: model.billTo_billingLocation,
        divisionId: model.billTo_divisionId,
        division: model.billTo_division,
        locationTypeId: model.billTo_locationTypeId,
        locationType: model.billTo_locationType,

        contact: {
          firstName: model.billTo_firstName,
          lastName: model.billTo_lastName,
          title: model.billTo_title,
          company: model.billTo_company,
          emailAddresses: model.billTo_emailAddresses,
          phoneNumber: model.billTo_phoneNumber,
          phoneExt: model.billTo_phoneExt,
          mobilePhone: model.billTo_mobilePhone,
          altPhoneNumber: model.billTo_altPhoneNumber,
          altPhoneExt: model.billTo_altPhoneExt,
          street1: model.billTo_address1,
          street2: model.billTo_address2,
          city: model.billTo_city,
          stateAbbreviation: model.billTo_state,
          postalCode: model.billTo_postalCode,
          county: model.billTo_county,
          latitude: model.externalLocationId ? 0 : model.billTo_latitude,
          longitude: model.externalLocationId ? 0 : model.billTo_longitude,
          externalLinkId:
            model.billTo_externalLinkId ||
            model.billTo_externalLocationId ||
            '',
          externalLinkType: model.billTo_externalLinkType || 'BillTo',
          contactTime: model.preferredTimeOfContact.join(','),
          contactMethod: model.preferredMethodOfContact,

          locationCode: model.billTo_externalLocationCode || '',
          externalLocationId: model.billTo_externalLocationId || '',
        },
      },
      //endregion
    ],
    locationSameAsBillTo: model.locationSameAsBillTo,
    locationTypeId: model.locationTypeId,
    locationType: model.locationType,
    nextActionDate: model.nextActionDate,
    note: model.note,
    noteCode:
      model.note?.length > 0 && model.addNoteToPestPac ? model.noteCode : '',
    originalContactMethod: model.originalContactMethod,
    preferredMethodOfContact: model.preferredMethodOfContact,
    preferredTimeOfContact: model.preferredTimeOfContact.join(','),
    ...model.referral,
    referralDiscountType: model.referral
      ? model.referral.referralDiscountType
      : undefined,
    referralType: model.referral ? model.referral.referralType : undefined,
    salesFunnelId: Number(model.salesFunnelId),
    salesFunnelStageId: Number(model.salesFunnelStageId),
    salesTeamId: Number(model.salesTeamId),
    submittedBy: model.submittedBy,
    tags: model.tags,
    targetPests: JSON.stringify(model.targetPests),
    type: model.type ?? 'Manual',
    updateCoreLocation: updateCoreLocation,
    viewDetail: model.viewDetail,
    ...getDisqualifiedReason(model),
  }
}

export interface ILeadLocationInfo {
  branch: string | undefined
  externalLocationId: string | undefined
  externalLocationCode: string | undefined
  externalLocationUrl: string | undefined
}

export interface FieldToCheck {
  name: string
  errorFields: string[]
}
